/**
 * Die Marke, die sagt: «Es wurde gerade angemeldet.»
 *
 * ===========================================================================
 * WARUM EINE MARKE UND NICHT EIN ZUSTAND IN REACT
 * ===========================================================================
 *
 * Das Startbild soll **nach einer echten Anmeldung** erscheinen und sonst nie
 * — nicht beim Neuladen, nicht beim Seitenwechsel, nicht beim Entsperren einer
 * schon bestehenden Sitzung. Der angemeldete Zustand allein trägt diese
 * Unterscheidung nicht: `App.tsx` erreicht ihn beim Neuladen genauso wie nach
 * dem Anmelden, weil `GET /auth/me` in beiden Fällen dasselbe antwortet. Der
 * Unterschied liegt ausschliesslich im **Ereignis** — jemand hat sich soeben
 * angemeldet —, und dieses Ereignis kennt nur die Anmeldemaske.
 *
 * Deshalb setzt die Anmeldung eine Marke, und die angemeldete Anwendung liest
 * sie beim Einhängen und verwirft sie sofort danach — ein zweites Einhängen
 * findet nichts mehr.
 *
 * **Lesen und Verwerfen sind zwei Funktionen, und das ist Absicht.** Gelesen
 * wird im Anfangswert eines Zustands, damit das Bild schon im ERSTEN
 * Renderdurchgang steht und die Anwendung darunter nicht für einen Lidschlag
 * sichtbar wird; verworfen wird in einem Effekt, weil das Rendern selbst
 * nichts ausserhalb von React verändern soll. Eine Funktion, die beides täte,
 * müsste im Anfangswert stehen und schriebe damit beim Rendern.
 *
 * ===========================================================================
 * WARUM `sessionStorage` UND NICHT `localStorage`
 * ===========================================================================
 *
 * `sessionStorage` gehört dem Browsertab und endet mit ihm. Eine Marke in
 * `localStorage` überlebte das Schliessen des Fensters und zeigte das
 * Startbild irgendwann später ein zweites Mal — in einem anderen Tab, am
 * nächsten Tag, ohne dass sich jemand angemeldet hätte. Dieselbe Wahl trifft
 * `app/ErrorBoundary.tsx` für seinen Schleifenschutz, aus demselben Grund.
 *
 * **Ein fehlgeschlagener Zugriff ist kein Fehlerfall.** In einem privaten
 * Fenster mit abgeschaltetem Speicher wirft `sessionStorage` schon beim Lesen.
 * Dann entfällt das Startbild — und die Anwendung startet, als hätte es nie
 * eines gegeben. Ein Startbild ist nichts, wofür eine Anmeldung scheitern
 * darf.
 */

/**
 * Der Schlüssel im Sitzungsspeicher.
 *
 * Der Vorsatz ist Absicht, dieselbe Begründung wie bei
 * `FARBSCHEMA_SCHLUESSEL`: Der Speicher gehört der Herkunft und nicht dieser
 * Anwendung allein.
 */
export const SPLASH_MARKE_SCHLUESSEL = 'fameacc.splash.faellig';

/** Merkt, dass soeben eine Anmeldung stattgefunden hat. */
export function splashFaelligMerken(): void {
  try {
    window.sessionStorage.setItem(SPLASH_MARKE_SCHLUESSEL, '1');
  } catch {
    // Kein Speicher: kein Startbild. Siehe Kopfbemerkung.
  }
}

/** Liest die Marke, ohne sie anzufassen. */
export function splashFaelligLesen(): boolean {
  try {
    return window.sessionStorage.getItem(SPLASH_MARKE_SCHLUESSEL) === '1';
  } catch {
    return false;
  }
}

/** Verwirft die Marke. Mehrfach aufzurufen schadet nicht. */
export function splashMarkeVerwerfen(): void {
  try {
    window.sessionStorage.removeItem(SPLASH_MARKE_SCHLUESSEL);
  } catch {
    // Kein Speicher: Es gab auch nichts zu merken. Siehe Kopfbemerkung.
  }
}
